import { routeFormData2CreateRouteData } from "@/components/RouteForm2/createRouteData";
import { orderService } from "@/services/OrderService";
import { OrderStatus } from "@/types/order/order.common";
import { RouteStatus } from "@/types/route/route.common";
import { ServiceVehicleStatus } from "@/types/serviceVehicle/serviceVehicle.common";
import { message } from "antd";
import dayjs from "dayjs";
import { makeAutoObservable } from "mobx";
import { TableSelectStore } from "@/components/TableSelect/TableSelectStore";
import { DataTableStore } from "./DataTableStore";
import { ChargingStationStatus } from "@/types/chargingStation/chargingStation.common";
import { SearchFilter } from "@/components/filters";
/**
 * SingleRouteStore - manages individual route operations (CRUD, forms)
 * Following SOLID principles and project patterns
 * - Single Responsibility: Only manages individual route operations
 * - Dependency Inversion: Depends on abstractions (interfaces) not concrete implementations
 * - Open/Closed: Easy to extend with new functionality
 */
export class SingleRouteStore2 {
    constructor(routeService, routePointsService, technicianService, serviceVehicleService, chargingStationService, workShiftsService, routeBuilderStore) {
        this.routeService = routeService;
        this.routePointsService = routePointsService;
        this.technicianService = technicianService;
        this.serviceVehicleService = serviceVehicleService;
        this.chargingStationService = chargingStationService;
        this.workShiftsService = workShiftsService;
        this.routeBuilderStore = routeBuilderStore;
        // Loading and error state
        this.isLoading = false;
        this.error = null;
        // Individual route data
        this.route = null;
        this.routePoints = [];
        // Override for quick order start time
        this._quickOrderStartTime = null;
        this.technicianSelectStore = null;
        this.serviceVehicleSelectStore = null;
        this.chargingStationSelectStore = null;
        makeAutoObservable(this);
    }
    // === INDIVIDUAL ROUTE OPERATIONS ===
    /**
     * Create a new route
     */
    async saveRoute(formData) {
        if (!this.routeBuilderStore?.isValidRoute) {
            message.error(this.routeBuilderStore?.validationMessage);
            return;
        }
        if (this.routeBuilderStore?.isCalculating) {
            message.error("Маршрут рассчитывается");
            return;
        }
        // Сохраняем только активные (не soft-deleted) точки маршрута
        const selectedRoutePoints = this.routeBuilderStore?.activeItems || [];
        if (selectedRoutePoints.length < 2) {
            message.error("Маршрут должен содержать минимум 2 точки (базу и заказ)");
            return;
        }
        this.setIsLoading(true);
        this.clearError();
        try {
            const routeData = await routeFormData2CreateRouteData(formData);
            let routeId = this.route?.id;
            const isCreation = !routeId;
            if (isCreation) {
                // Атомарное создание: один RPC вызов — PostgreSQL транзакция
                const res = await this.routeService.createWithPoints(routeData, selectedRoutePoints);
                this.setRoute(res);
            }
            else {
                const updatedRoute = await this.routeService.update(routeId, routeData);
                await this.routePointsService.update(routeId, selectedRoutePoints);
                this.setRoute(updatedRoute);
            }
            message.success(isCreation ? "Маршрут успешно создан" : "Маршрут успешно обновлен");
        }
        catch (error) {
            this.setError(this.getErrorMessage(error, "Failed to create route"));
        }
        finally {
            this.setIsLoading(false);
        }
    }
    async planRoute() {
        try {
            if (!this.route)
                throw Error("route data is empty");
            if (!this.routeBuilderStore?.isValidRoute) {
                message.error(this.routeBuilderStore?.validationMessage);
                return;
            }
            if (this.routeBuilderStore?.isCalculating) {
                message.error("Маршрут рассчитывается");
                return;
            }
            const routeId = this.route.id;
            const updatedRoute = await this.routeService.update(routeId, {
                ...this.route,
                status: RouteStatus.PLANNED,
            });
            this.setRoute(updatedRoute);
            message.success("Маршрут готов к выполнению");
        }
        catch (error) {
            this.setError(this.getErrorMessage(error, "Failed to update route"));
        }
    }
    /**
     * Cancel route (dispatcher action):
     * - route.status → CANCELLED
     * - route_points soft-deleted (isDeleted = true)
     * - Orders are NOT cancelled — remain available for reassignment
     */
    async cancelRoute(reason) {
        if (!this.route)
            return;
        this.setIsLoading(true);
        this.clearError();
        try {
            const routeId = this.route.id;
            const cancelled = await this.routeService.cancel(routeId, reason);
            this.setRoute(cancelled);
            // Перезагружаем точки маршрута (теперь с isDeleted=true) и RouteBuilder,
            // чтобы диспетчер видел отменённые точки в истории маршрута
            await this.loadRoute(routeId);
            await this.routeBuilderStore.init(routeId);
            message.success("Маршрут отменён");
        }
        catch (error) {
            this.setError(this.getErrorMessage(error, "Failed to cancel route"));
        }
        finally {
            this.setIsLoading(false);
        }
    }
    /**
     * Cancel route by ID (used from list page where route is not loaded into store).
     * Same logic as cancelRoute() but accepts routeId directly.
     */
    async cancelRouteById(routeId, reason) {
        this.setIsLoading(true);
        this.clearError();
        try {
            await this.routeService.cancel(routeId, reason);
        }
        catch (error) {
            this.setError(this.getErrorMessage(error, "Failed to cancel route"));
            throw error;
        }
        finally {
            this.setIsLoading(false);
        }
    }
    /**
     * Create a new route and immediately set it to PLANNED status
     * Note: No strict validation - user sees warnings on page and can proceed anyway
     */
    async saveAndPlanRoute(formData) {
        if (!this.routeBuilderStore?.isValidRoute) {
            message.error(this.routeBuilderStore?.validationMessage);
            return;
        }
        if (this.routeBuilderStore?.isCalculating) {
            message.error("Маршрут рассчитывается");
            return;
        }
        // Сохраняем только активные (не soft-deleted) точки маршрута
        const selectedRoutePoints = this.routeBuilderStore?.activeItems || [];
        if (selectedRoutePoints.length < 2) {
            message.error("Маршрут должен содержать минимум 2 точки (базу и заказ)");
            return;
        }
        this.setIsLoading(true);
        this.clearError();
        try {
            const routeData = await routeFormData2CreateRouteData(formData);
            // Атомарное создание: один RPC вызов — PostgreSQL транзакция
            const createdRoute = await this.routeService.createWithPoints(routeData, selectedRoutePoints);
            // Immediately set status to PLANNED
            const { id: _, ...routeDataWithoutId } = createdRoute;
            const plannedRoute = await this.routeService.update(createdRoute.id, {
                ...routeDataWithoutId,
                status: RouteStatus.PLANNED,
            });
            this.setRoute(plannedRoute);
            message.success("Маршрут успешно создан и подтверждён");
        }
        catch (error) {
            this.setError(this.getErrorMessage(error, "Failed to create and plan route"));
        }
        finally {
            this.setIsLoading(false);
        }
    }
    /**
     * Delete a route
     */
    async deleteRoute(routeId) {
        this.setIsLoading(true);
        this.clearError();
        try {
            // Delete route_points first, then the route itself
            await this.routePointsService.delete(routeId);
            await this.routeService.delete(routeId);
        }
        catch (error) {
            this.setError(this.getErrorMessage(error, "Failed to delete route"));
            throw error;
        }
        finally {
            this.setIsLoading(false);
        }
    }
    async init(routeId) {
        this.setIsLoading(true);
        this.clearError();
        try {
            const [route, routePoints] = await Promise.all([
                routeId ? this.routeService.get(routeId) : null,
                routeId ? this.routePointsService.get(routeId) : null,
            ]);
            this.setRoute(route);
            this.setRoutePoints(routePoints || []);
            // Создаем TableSelectStore для выбора техника
            this.setTechnicianSelectStore(new TableSelectStore(new DataTableStore({
                dataLoader: {
                    loadData: (params) => this.technicianService.getTechniciansWithParams(params),
                },
            }), {
                dataLoader: () => this.route
                    ? this.technicianService.getTechniciansByIds([
                        this.route.idTechnician,
                    ])
                    : Promise.resolve([]),
            }, [
                {
                    id: "technician-search-filter",
                    Cmp: SearchFilter,
                    props: {
                        name: "search",
                        label: "Поиск",
                        placeholder: "Поиск по имени, email...",
                        allowClear: true,
                        showSearchIcon: true,
                        onChange: (value) => this.technicianSelectStore?.filterStore?.updateFilter("search", value),
                    },
                    visible: true,
                    order: 1,
                },
            ]));
            this.setServiceVehicleSelectStore(new TableSelectStore(new DataTableStore({
                dataLoader: {
                    loadData: (params) => this.serviceVehicleService.getVehiclesWithParams({
                        ...params,
                        filters: {
                            ...params.filters,
                            status: ServiceVehicleStatus.AVAILABLE,
                        },
                    }),
                },
            }), {
                dataLoader: () => this.route && this.route.idServiceVehicle
                    ? this.serviceVehicleService.getVehiclesByIds([
                        this.route.idServiceVehicle,
                    ])
                    : Promise.resolve([]),
            }, [
                {
                    id: "service-vehicle-search-filter",
                    Cmp: SearchFilter,
                    props: {
                        name: "search",
                        label: "Поиск",
                        placeholder: "Поиск по номеру, марке...",
                        allowClear: true,
                        showSearchIcon: true,
                        onChange: (value) => this.serviceVehicleSelectStore?.filterStore?.updateFilter("search", value),
                    },
                    visible: true,
                    order: 1,
                },
            ]));
            this.setChargingStationSelectStore(new TableSelectStore(new DataTableStore({
                dataLoader: {
                    loadData: (params) => this.chargingStationService.getStationsWithParams({
                        ...params,
                        filters: {
                            ...params.filters,
                            status: ChargingStationStatus.AVAILABLE,
                        },
                    }),
                },
            }), {
                dataLoader: () => this.route && this.route.idChargingStation
                    ? this.chargingStationService.getStationsByIds([
                        this.route.idChargingStation,
                    ])
                    : Promise.resolve([]),
            }, [
                {
                    id: "charging-station-search-filter",
                    Cmp: SearchFilter,
                    props: {
                        name: "search",
                        label: "Поиск",
                        placeholder: "Поиск по названию, адресу...",
                        allowClear: true,
                        showSearchIcon: true,
                        onChange: (value) => this.chargingStationSelectStore?.filterStore?.updateFilter("search", value),
                    },
                    visible: true,
                    order: 1,
                },
            ]));
        }
        catch (error) {
            this.setError(this.getErrorMessage(error, "Failed to load route"));
            throw error;
        }
        finally {
            this.setIsLoading(false);
        }
    }
    /**
     * Load a specific route with its points
     */
    async loadRoute(routeId) {
        this.setIsLoading(true);
        this.clearError();
        try {
            const [route, routePoints] = await Promise.all([
                this.routeService.get(routeId),
                this.routePointsService.get(routeId),
            ]);
            this.setRoute(route);
            this.setRoutePoints(routePoints);
            return route;
        }
        catch (error) {
            this.setError(this.getErrorMessage(error, "Failed to load route"));
            throw error;
        }
        finally {
            this.setIsLoading(false);
        }
    }
    /**
     * Clear route-specific data
     */
    clearRouteData() {
        this.setRoute(null);
        this.setRoutePoints([]);
        this._quickOrderStartTime = null;
        this.technicianSelectStore?.reset();
        this.serviceVehicleSelectStore?.reset();
        this.chargingStationSelectStore?.reset();
        this.clearError();
    }
    get initialFormData() {
        if (this.isLoading)
            return {};
        if (!this.route) {
            return {
                // Use quick order start time if available, otherwise default to tomorrow 9:00
                plannedStartTime: this._quickOrderStartTime ?? dayjs().add(1, "day").hour(9).minute(0),
                expectedArrivalTime: null,
                idTechnician: "",
                idServiceVehicle: undefined,
                idChargingStation: undefined,
            };
        }
        return {
            plannedStartTime: dayjs(this.route.plannedStartTime),
            expectedArrivalTime: null,
            idTechnician: this.route.idTechnician,
            idServiceVehicle: this.route.idServiceVehicle || null,
            idChargingStation: this.route.idChargingStation || null,
        };
    }
    /**
     * Set start time for quick order (used when navigating from QuickOrdersPage)
     */
    setQuickOrderStartTime(startTime) {
        this._quickOrderStartTime = startTime;
    }
    // === PRIVATE SETTERS (following MobX patterns) ===
    setRoute(route) {
        this.route = route;
    }
    setRoutePoints(routePoints) {
        this.routePoints = routePoints;
    }
    setError(error) {
        this.error = error;
    }
    setIsLoading(loading) {
        this.isLoading = loading;
    }
    clearError() {
        this.error = null;
    }
    // === UTILITY METHODS ===
    /**
     * Get error message from error object
     */
    getErrorMessage(error, defaultMessage) {
        if (error instanceof Error) {
            return error.message;
        }
        if (typeof error === "string") {
            return error;
        }
        return defaultMessage;
    }
    /**
     * Check if route can be edited (only PLANNED routes)
     * RQ223: Only PLANNED routes can be edited
     */
    canEditRoute(routeStatus) {
        return (routeStatus === RouteStatus.CREATED || routeStatus === RouteStatus.PLANNED);
    }
    /**
     * Check if route can be tracked (only IN_PROGRESS routes)
     * RQ207: Track action available for IN_PROGRESS status
     */
    get canTrackRoute() {
        return this.route?.status === RouteStatus.IN_PROGRESS;
    }
    get canSetToPlanned() {
        return this.route?.status === RouteStatus.CREATED;
    }
    /**
     * Dispatcher can cancel routes in CREATED, PLANNED or IN_PROGRESS status.
     * COMPLETED and CANCELLED routes cannot be cancelled from dispatcher UI.
     */
    get canCancelRoute() {
        return (this.route?.status === RouteStatus.CREATED ||
            this.route?.status === RouteStatus.PLANNED ||
            this.route?.status === RouteStatus.IN_PROGRESS);
    }
    /**
     * Cancel a specific order within this route (dispatcher action).
     * Sets order status → DISPATCHER_CANCELLED.
     * DB trigger trg_order_cancellation_route_sync handles:
     * - soft-delete of the route_point
     * - route status update (CANCELLED if no orders left, COMPLETED if no future orders in IN_PROGRESS)
     * - charging session completion if charging was in progress
     */
    async cancelOrderInRoute(orderId) {
        if (!this.route)
            return;
        this.setIsLoading(true);
        this.clearError();
        try {
            await orderService.changeOrderStatus(orderId, OrderStatus.DISPATCHER_CANCELLED);
            message.success("Заказ отменён");
            const routeId = this.route.id;
            // Reload route — DB trigger already updated route_points and route status
            await this.loadRoute(routeId);
            // Также перезагружаем RouteBuilder, чтобы показать отменённую точку
            await this.routeBuilderStore.init(routeId);
        }
        catch (error) {
            this.setError(this.getErrorMessage(error, "Failed to cancel order in route"));
        }
        finally {
            this.setIsLoading(false);
        }
    }
    /**
     * Returns list of { orderNumber, orderId } for route points that have orders.
     * Used to display order numbers in the route page header.
     */
    get orderNumbers() {
        return this.routePoints
            .filter((rp) => rp.idOrder)
            .map((rp, idx) => ({
            orderNumber: rp.orderNumber ?? `Заказ ${idx + 1}`,
            orderId: rp.idOrder,
        }));
    }
    /**
     * Get route form title based on current state
     */
    getRouteFormTitle() {
        if (!this.route) {
            return "Создание маршрута";
        }
        const canEdit = this.canEditRoute(this.route.status);
        return canEdit ? "Редактирование маршрута" : "Просмотр маршрута";
    }
    setTechnicianSelectStore(technicianSelectStore) {
        this.technicianSelectStore = technicianSelectStore;
    }
    setServiceVehicleSelectStore(serviceVehicleSelectStore) {
        this.serviceVehicleSelectStore = serviceVehicleSelectStore;
    }
    setChargingStationSelectStore(chargingStationSelectStore) {
        this.chargingStationSelectStore = chargingStationSelectStore;
    }
    async loadWorkShifts(technicianId) {
        try {
            if (!technicianId)
                return [];
            const workShifts = await this.workShiftsService.getShiftsByTechnicianId(technicianId);
            return workShifts;
        }
        catch (error) {
            this.setError(this.getErrorMessage(error, "Failed to load work shifts"));
            return [];
        }
    }
}
